import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Globe, ChevronDown, Lock, Unlock, Info, RotateCcw, X, List, Map as MapIcon, Search, Building2, Waves, Check } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { REGIONS as ALL_REGIONS, REGION_COORDS, TYPE_COLORS, CountryBoundaries, MarketZoneBoundaries, MapClickHandler, MapFlyTo, MapInvalidator, createZoneIcon, findNearestCountry } from './MarketZoneMap';
import { useTheme } from '../../hooks/useTheme';
// --- Types ---
export interface LocationOverride {
  regionId: string;
  regionName: string;
  regionType: 'Country' | 'Market' | 'City' | 'Offshore';
}
interface Region {
  id: string;
  name: string;
  type: 'Country' | 'Market' | 'City' | 'Offshore';
  parentId?: string;
}
// Use the full regions list from MarketZoneMap
const REGIONS: Region[] = ALL_REGIONS as Region[];
// Type filter tabs config
const TYPE_TABS: {
  id: string;
  label: string;
}[] = [{
  id: 'Country',
  label: 'Countries'
}, {
  id: 'Market',
  label: 'Market Zones'
}];
interface LocationSelectorProps {
  onOverrideChange?: (override: LocationOverride | null) => void;
  /** 'override' = workspace mode with lock/override toggle; 'page' = standalone selector */
  mode?: 'override' | 'page';
  /** Region id to pre-select (e.g. 'de' for Germany) */
  defaultRegionId?: string;
}
export function LocationSelector({
  onOverrideChange,
  mode = 'override',
  defaultRegionId
}: LocationSelectorProps) {
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const isPageMode = mode === 'page';
  const [open, setOpen] = useState(false);
  const [overrideActive, setOverrideActive] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Region | null>(() => defaultRegionId ? REGIONS.find(r => r.id === defaultRegionId) || null : null);
  // Tab & filter state
  const [activeTab, setActiveTab] = useState<'list' | 'map'>('map');
  const [activeType, setActiveType] = useState<string>('Country');
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Auto-focus search when opening list tab
  useEffect(() => {
    if (open && activeTab === 'list' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, activeTab]);
  // Click outside to close
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target) && triggerRef.current && !triggerRef.current.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  // Type counts
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    REGIONS.forEach(r => {
      counts[r.type] = (counts[r.type] || 0) + 1;
    });
    return counts;
  }, []);
  // Filtered regions for list view
  const filteredRegions = useMemo(() => {
    let filtered = REGIONS.filter(r => r.type === activeType);
    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter(r => r.name.toLowerCase().includes(lower));
    }
    return filtered;
  }, [search, activeType]);
  // Override Logic
  const buildOverride = (region: Region): LocationOverride => ({
    regionId: region.id,
    regionName: region.name,
    regionType: region.type
  });
  const toggleOverride = () => {
    const next = !overrideActive;
    setOverrideActive(next);
    if (next && selectedLocation) {
      onOverrideChange?.(buildOverride(selectedLocation));
    } else {
      onOverrideChange?.(null);
    }
  };
  // Notify parent when selection changes
  useEffect(() => {
    if (isPageMode) {
      if (selectedLocation) {
        onOverrideChange?.(buildOverride(selectedLocation));
      } else {
        onOverrideChange?.(null);
      }
    } else {
      if (overrideActive && selectedLocation) {
        onOverrideChange?.(buildOverride(selectedLocation));
      } else if (overrideActive && !selectedLocation) {
        setOverrideActive(false);
        onOverrideChange?.(null);
      }
    }
  }, [selectedLocation, overrideActive, onOverrideChange, isPageMode]);
  const resetSelection = () => {
    setSelectedLocation(null);
    if (overrideActive) {
      setOverrideActive(false);
      onOverrideChange?.(null);
    }
  };
  const handleSelectRegion = useCallback((region: Region) => {
    setSelectedLocation(region);
    setActiveTab('map');
  }, []);
  const handleBoundarySelect = useCallback((regionId: string) => {
    const region = REGIONS.find(r => r.id === regionId);
    if (region) {
      handleSelectRegion(region);
    }
  }, [handleSelectRegion]);
  const handleMapClick = useCallback((lat: number, lng: number) => {
    const nearest = findNearestCountry(lat, lng);
    if (nearest) {
      handleSelectRegion(nearest as Region);
    }
  }, [handleSelectRegion]);
  // Helpers
  const getIconForType = (type: string) => {
    switch (type) {
      case 'Market':
        return <Globe size={12} className="text-blue-400" data-id="element-1459" />;
      case 'Country':
        return <Globe size={12} className="text-emerald-400" data-id="element-1460" />;
      case 'City':
        return <Building2 size={12} className="text-amber-400" data-id="element-1461" />;
      case 'Offshore':
        return <Waves size={12} className="text-cyan-400" data-id="element-1462" />;
      default:
        return <Globe size={12} data-id="element-1463" />;
    }
  };
  const getPanelPosition = () => {
    if (!triggerRef.current) return {
      top: 0,
      left: 0
    };
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: rect.left
    };
  };
  // Fly target for map
  const flyTarget = useMemo(() => {
    if (selectedLocation && REGION_COORDS[selectedLocation.id]) {
      return REGION_COORDS[selectedLocation.id];
    }
    return null;
  }, [selectedLocation]);
  // Map regions for markers (only show when not using boundaries)
  const mapRegions = useMemo(() => {
    return REGIONS.filter(r => r.type === activeType).filter(r => REGION_COORDS[r.id]);
  }, [activeType]);
  const tileUrl = isLight ? 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png' : 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
  const dropdownBg = isLight ? 'rgba(255, 255, 255, 0.97)' : 'rgba(11, 17, 32, 0.97)';
  const borderColor = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  return <>
      {/* Trigger Button */}
      <button ref={triggerRef} onClick={() => setOpen(!open)} className={`group jua-toolbar-btn flex items-center gap-2 h-8 px-3 rounded-lg text-xs font-medium border transition-all ${!isPageMode && overrideActive ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : open ? isLight ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : isLight ? 'bg-black/[0.04] border-black/[0.08] text-slate-600 hover:bg-[#EC4899]/10 hover:border-[#EC4899]/30 hover:text-[#EC4899]' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-[#EC4899]/10 hover:border-[#EC4899]/30 hover:text-[#EC4899]'}`} data-id="element-1464">
        {!isPageMode && overrideActive ? <Lock size={12} className="text-amber-400" data-id="element-1465" /> : <Globe size={12} className={`transition-colors ${open ? 'text-[#EC4899]' : isLight ? 'text-slate-400 group-hover:text-[#EC4899]' : 'text-slate-500 group-hover:text-[#EC4899]'}`} data-id="element-1466" />}
        <span className="font-bold" data-id="element-1467">
          {selectedLocation ? selectedLocation.name : isPageMode ? 'Select Region' : 'Region Override'}
        </span>
        {!isPageMode && overrideActive && <>
            <span className="text-amber-500/40" data-id="element-1468">·</span>
            <span className="text-amber-400/70 text-[10px]" data-id="element-1469">Override</span>
          </>}
        {selectedLocation && (isPageMode || !overrideActive) && <>
            <span className={`transition-colors ${isLight ? 'text-slate-300 group-hover:text-[#EC4899]/50' : 'text-slate-600 group-hover:text-[#EC4899]/50'}`} data-id="element-1470">
              ·
            </span>
            <span className={`transition-colors ${isLight ? 'text-slate-400 group-hover:text-[#EC4899]/80' : 'text-slate-500 group-hover:text-[#EC4899]/80'}`} data-id="element-1471">
              {selectedLocation.type}
            </span>
          </>}
        <ChevronDown size={12} className={`transition-transform transition-colors ${open ? 'rotate-180' : ''} ${!isPageMode && overrideActive ? 'text-amber-500' : open ? 'text-[#EC4899]' : isLight ? 'text-slate-400 group-hover:text-[#EC4899]' : 'text-slate-500 group-hover:text-[#EC4899]'}`} data-id="element-1472" />
      </button>

      {/* Dropdown Panel */}
      {open && ReactDOM.createPortal(<div ref={panelRef} className="jua-location-panel fixed rounded-xl shadow-2xl border overflow-hidden z-[9999] flex flex-col" style={{
      background: dropdownBg,
      backdropFilter: 'blur(40px) saturate(180%)',
      WebkitBackdropFilter: 'blur(40px) saturate(180%)',
      borderColor,
      top: getPanelPosition().top,
      left: getPanelPosition().left,
      width: 420,
      maxHeight: 560,
      boxShadow: isLight ? '0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)' : '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)'
    }} data-id="element-1473">
            {/* Inner glow */}
            <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
        background: isLight ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%, rgba(255,255,255,0.01) 100%)'
      }} data-id="element-1474" />

            {/* Header */}
            <div className="relative flex items-center justify-between px-4 py-3 border-b" style={{
        borderColor
      }} data-id="element-1475">
              <div className="flex items-center gap-3 min-w-0" data-id="element-1476">
                <h3 className="text-sm font-semibold" style={{
            color: isLight ? '#0f172a' : '#ffffff'
          }} data-id="element-1477">
                  {isPageMode ? 'Select Location' : 'Select Location'}
                </h3>
                {selectedLocation && <span className="text-[10px] font-medium" style={{
            color: isLight ? '#94a3b8' : '#64748b'
          }} data-id="element-1478">
                    {selectedLocation.name}
                  </span>}
              </div>
              <div className="flex items-center gap-2" data-id="element-1479">
                {/* Map/List toggle */}
                <div className="flex items-center p-0.5 rounded-lg border" style={{
            backgroundColor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)',
            borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'
          }} data-id="element-1480">
                  <button onClick={() => setActiveTab('map')} className="flex items-center justify-center px-2 py-1 rounded-md text-xs font-medium transition-all" style={{
              backgroundColor: activeTab === 'map' ? isLight ? '#ffffff' : 'rgba(255,255,255,0.1)' : 'transparent',
              color: activeTab === 'map' ? isLight ? '#0f172a' : '#ffffff' : isLight ? '#64748b' : '#94a3b8',
              boxShadow: activeTab === 'map' && isLight ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
            }} data-id="element-1481">
                    <MapIcon size={12} className="mr-1.5" data-id="element-1482" />
                    Map
                  </button>
                  <button onClick={() => setActiveTab('list')} className="flex items-center justify-center px-2 py-1 rounded-md text-xs font-medium transition-all" style={{
              backgroundColor: activeTab === 'list' ? isLight ? '#ffffff' : 'rgba(255,255,255,0.1)' : 'transparent',
              color: activeTab === 'list' ? isLight ? '#0f172a' : '#ffffff' : isLight ? '#64748b' : '#94a3b8',
              boxShadow: activeTab === 'list' && isLight ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
            }} data-id="element-1483">
                    <List size={12} className="mr-1.5" data-id="element-1484" />
                    List
                  </button>
                </div>
                <button onClick={resetSelection} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium transition-colors" style={{
            color: isLight ? '#64748b' : '#94a3b8'
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }} data-id="element-1485">
                  <RotateCcw size={11} data-id="element-1486" /> Reset
                </button>
                <button onClick={() => setOpen(false)} className="p-1 rounded-md transition-colors" style={{
            color: isLight ? '#94a3b8' : '#64748b'
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }} data-id="element-1487">
                  <X size={14} data-id="element-1488" />
                </button>
              </div>
            </div>

            {/* Type Filter Tabs */}
            <div className="flex items-center gap-1.5 px-3 py-2 overflow-x-auto border-b scrollbar-hide" style={{
        borderColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)',
        backgroundColor: isLight ? 'rgba(248,250,252,0.5)' : 'rgba(15,23,42,0.5)'
      }} data-id="element-1489">
              {TYPE_TABS.map(tab => {
          const isActive = activeType === tab.id;
          const count = typeCounts[tab.id] || 0;
          return <button key={tab.id} onClick={() => setActiveType(tab.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all" style={{
            backgroundColor: isActive ? isLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.12)' : isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
            color: isActive ? isLight ? '#0f172a' : '#f1f5f9' : isLight ? '#475569' : '#94a3b8'
          }} data-id="element-1490">
                    {tab.label}
                    <span className="text-[10px]" style={{
              color: isActive ? isLight ? '#64748b' : '#94a3b8' : isLight ? '#94a3b8' : '#64748b'
            }} data-id="element-1491">
                      {count}
                    </span>
                  </button>;
        })}
            </div>

            {/* Content Area */}
            <div className="relative flex flex-col flex-1 overflow-hidden h-[320px]" data-id="element-1492">
              {activeTab === 'map' /* ── Map View with Boundaries ── */ ? <div className="h-full w-full relative flex-1" style={{
          height: 320
        }} data-id="element-1493">
                  <MapContainer center={[50, 10]} zoom={4} className="h-full w-full" zoomControl={true} attributionControl={false} style={{
            height: '100%',
            width: '100%',
            background: isLight ? '#e2e8f0' : '#0a1628'
          }} data-id="element-1494">
                    <TileLayer url={tileUrl} data-id="element-1495" />
                    <MapInvalidator data-id="element-1496" />
                    <MapFlyTo target={flyTarget} data-id="element-1497" />
                    <MapClickHandler onClickLocation={handleMapClick} data-id="element-1498" />

                    {/* Boundary polygons based on active type */}
                    {activeType === 'Market' ? <MarketZoneBoundaries selectedId={selectedLocation?.id || null} isLight={isLight} onSelectRegion={handleBoundarySelect} data-id="element-1499" /> : <CountryBoundaries selectedId={selectedLocation?.id || null} isLight={isLight} onSelectRegion={handleBoundarySelect} boundaryType="Country" data-id="element-1500" />}

                    {/* Only show dot markers for types without boundaries (City, Offshore) */}
                    {activeType !== 'Country' && activeType !== 'Market' && mapRegions.map(region => {
              const coords = REGION_COORDS[region.id];
              if (!coords) return null;
              const isRegionSelected = selectedLocation?.id === region.id;
              return <Marker key={region.id} position={[coords.lat, coords.lng]} icon={createZoneIcon(region.type, isRegionSelected, isLight)} eventHandlers={{
                click: () => handleSelectRegion(region)
              }} data-id="element-1501" />;
            })}
                  </MapContainer>

                  {/* Hint Overlay */}
                  <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg text-[10px] font-medium z-[400] shadow-sm pointer-events-none" style={{
            background: isLight ? 'rgba(255,255,255,0.9)' : 'rgba(15, 23, 42, 0.85)',
            color: isLight ? '#64748b' : '#94a3b8',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: `1px solid ${borderColor}`
          }} data-id="element-1502">
                    {activeType === 'Country' ? 'Click map or boundaries to select country' : 'Click boundaries or markers to select market zone'}
                  </div>

                  {/* Selected indicator */}
                  {selectedLocation && <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium text-[#EC4899] z-[400]" style={{
            background: isLight ? 'rgba(255,255,255,0.9)' : 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(236,72,153,0.2)'
          }} data-id="element-1503">
                      <Check size={10} data-id="element-1504" />
                      {selectedLocation.name}
                    </div>}
                </div> /* ── List View ── */ : <div className="flex flex-col h-full" data-id="element-1505">
                  {/* Search */}
                  <div className="p-3 border-b sticky top-0 z-10" style={{
            borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
            backgroundColor: isLight ? 'rgba(255,255,255,0.95)' : 'rgba(15,23,42,0.95)',
            backdropFilter: 'blur(12px)'
          }} data-id="element-1506">
                    <div className="relative" data-id="element-1507">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{
                color: isLight ? '#94a3b8' : '#64748b'
              }} data-id="element-1508" />
                      <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${activeType === 'Market' ? 'market zones' : 'countries'}...`} className="jua-location-search w-full h-9 pl-9 pr-3 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition-all" style={{
                backgroundColor: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.2)',
                borderColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
                color: isLight ? '#0f172a' : '#ffffff'
              }} data-id="element-1509" />
                      {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full transition-colors" style={{
                color: isLight ? '#64748b' : '#94a3b8'
              }} data-id="element-1510">
                          <X size={12} data-id="element-1511" />
                        </button>}
                    </div>
                  </div>

                  {/* List */}
                  <div className="flex-1 overflow-y-auto p-2" data-id="element-1512">
                    {filteredRegions.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-center py-12" style={{
              color: isLight ? '#94a3b8' : '#64748b'
            }} data-id="element-1513">
                        <Search size={24} className="mb-3 opacity-50" data-id="element-1514" />
                        <p className="text-sm font-medium" data-id="element-1515">No zones found</p>
                        <p className="text-xs mt-1 opacity-70" data-id="element-1516">
                          Try adjusting your search or filters
                        </p>
                      </div> : <div className="space-y-0.5" data-id="element-1517">
                        {filteredRegions.map(region => {
                const isRegionSelected = selectedLocation?.id === region.id;
                return <button key={region.id} onClick={() => handleSelectRegion(region)} className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all group" style={{
                  backgroundColor: isRegionSelected ? isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)' : 'transparent'
                }} onMouseEnter={e => {
                  if (!isRegionSelected) {
                    e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)';
                  }
                }} onMouseLeave={e => {
                  if (!isRegionSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }} data-id="element-1518">
                              <div className="flex items-center gap-3 min-w-0" data-id="element-1519">
                                <div className="flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0" style={{
                      backgroundColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
                      color: TYPE_COLORS[region.type] || '#94a3b8'
                    }} data-id="element-1520">
                                  {getIconForType(region.type)}
                                </div>
                                <div className="flex flex-col min-w-0" data-id="element-1521">
                                  <span className="text-sm font-medium truncate" style={{
                        color: isLight ? '#0f172a' : '#f8fafc'
                      }} data-id="element-1522">
                                    {region.name}
                                  </span>
                                  {region.parentId && <span className="text-[10px] truncate" style={{
                        color: isLight ? '#64748b' : '#94a3b8'
                      }} data-id="element-1523">
                                      {REGIONS.find(r => r.id === region.parentId)?.name}
                                    </span>}
                                </div>
                              </div>
                              {isRegionSelected && <Check size={16} className="flex-shrink-0 ml-3" style={{
                    color: isLight ? '#0f172a' : '#f8fafc'
                  }} data-id="element-1524" />}
                            </button>;
              })}
                      </div>}
                  </div>
                </div>}
            </div>

            {/* Footer */}
            <div className="relative px-4 py-2.5 flex items-center justify-between text-[11px] border-t" style={{
        borderColor
      }} data-id="element-1525">
              <div className="flex items-center gap-1.5" data-id="element-1526">
                <span style={{
            color: isLight ? '#64748b' : '#94a3b8'
          }} data-id="element-1527">
                  Selected:
                </span>
                <span className="font-bold" style={{
            color: selectedLocation ? isLight ? '#0f172a' : '#ffffff' : isLight ? '#94a3b8' : '#64748b'
          }} data-id="element-1528">
                  {selectedLocation ? selectedLocation.name : 'None'}
                </span>
              </div>

              {/* Override Toggle — only in workspace mode */}
              {!isPageMode && <button onClick={toggleOverride} disabled={!selectedLocation} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border" style={{
          backgroundColor: overrideActive ? 'rgba(245,158,11,0.15)' : !selectedLocation ? isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)' : isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)',
          borderColor: overrideActive ? 'rgba(245,158,11,0.3)' : !selectedLocation ? isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)' : isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
          color: overrideActive ? '#f59e0b' : !selectedLocation ? isLight ? '#94a3b8' : '#475569' : isLight ? '#64748b' : '#94a3b8',
          cursor: !selectedLocation ? 'not-allowed' : 'pointer'
        }} title={overrideActive ? 'Disable override — widgets revert to their own location' : 'Override all widgets with this location'} data-id="element-1529">
                  {overrideActive ? <Lock size={10} data-id="element-1530" /> : <Unlock size={10} data-id="element-1531" />}
                  {overrideActive ? 'Override Active' : 'Override All Widgets'}
                </button>}
            </div>
          </div>, document.body)}
    </>;
}